// src/store/meetings/meetings.slice.ts

import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { MeetingsState } from "./meetings.types";
import { TMeeting, TMeetingsFilters, TMeetingsResponse } from "@/models/meetings.model";
import { createMeeting, deleteMeeting, updateMeeting, setMeetingStatus } from "./meetings.thunks";
import { DEFAULT_PAGE_SIZE } from "@/constants";

const initialMeetingsState: MeetingsState = {
  initialized: false,
  loading: false,
  error: undefined,
  filters: {},
  entities: {},
  ids: [],
  total: 0,
  page: 1,
  limit: DEFAULT_PAGE_SIZE,
};

const meetingsSlice = createSlice({
  name: "meetings",
  initialState: initialMeetingsState,
  reducers: {
    resetMeetingsState: () => initialMeetingsState,

    setFilters(state, action: PayloadAction<Partial<TMeetingsFilters>>) {
      state.filters = { ...state.filters, ...action.payload };
      state.page = 1;
    },

    resetFilters(state) {
      state.filters = {};
      state.page = 1;
    },

    fetchMeetingsStart(state) {
      state.loading = true;
      state.error = undefined;
    },

    fetchMeetingsSuccess(state, action: PayloadAction<TMeetingsResponse>) {
      const { data, meta } = action.payload;

      state.loading = false;
      state.initialized = true;
      state.total = meta.totalCount;
      state.page = meta.page;
      state.limit = meta.pageSize;
      state.ids = data.map((meeting) => meeting.id);

      state.entities = {};
      data.forEach((meeting) => {
        state.entities[meeting.id] = meeting;
      });
    },

    fetchMeetingsError(state, action: PayloadAction<string>) {
      state.loading = false;
      state.error = action.payload;
    },

    meetingUpdated(
      state,
      action: PayloadAction<{ id: number; updates: Partial<TMeeting> }>
    ) {
      const { id, updates } = action.payload;
      if (state.entities[id]) {
        state.entities[id] = { ...state.entities[id], ...updates };
      }
    },

    meetingDeleted(state, action: PayloadAction<number>) {
      const id = action.payload;
      delete state.entities[id];
      state.ids = state.ids.filter((meetingId) => meetingId !== id);
      state.total = Math.max(0, state.total - 1);
    },

    setPage(state, action: PayloadAction<number>) {
      state.page = action.payload;
    },

    setLimit(state, action: PayloadAction<number>) {
      state.limit = action.payload;
      state.page = 1;
    },
  },

  extraReducers: (builder) => {
    // Create meeting
    builder.addCase(createMeeting.pending, (state) => {
      state.loading = true;
      state.error = undefined;
    });
    builder.addCase(createMeeting.fulfilled, (state) => {
      state.loading = false;
      // Don't mutate ids/entities here — we'll refetch page 1 from the thunk
      // to ensure correct ordering and pagination consistency
    });
    builder.addCase(createMeeting.rejected, (state, action) => {
      state.loading = false;
      state.error = action.error.message || "Failed to create meeting";
    });

    // Update meeting
    builder.addCase(updateMeeting.pending, (state) => {
      state.error = undefined;
    });
    builder.addCase(updateMeeting.fulfilled, (state, action) => {
      const meeting = action.payload;
      if (state.entities[meeting.id]) {
        state.entities[meeting.id] = meeting;
      }
    });
    builder.addCase(updateMeeting.rejected, (state, action) => {
      state.error = action.error.message || "Failed to update meeting";
    });

    // Set meeting status (no_show / cancelled)
    builder.addCase(setMeetingStatus.pending, (state) => {
      state.error = undefined;
    });
    builder.addCase(setMeetingStatus.fulfilled, (state, action) => {
      const meeting = action.payload;
      if (state.entities[meeting.id]) {
        state.entities[meeting.id] = meeting;
      }
    });
    builder.addCase(setMeetingStatus.rejected, (state, action) => {
      state.error = action.error.message || "Failed to update meeting status";
    });

    // Delete meeting
    builder.addCase(deleteMeeting.pending, (state) => {
      state.error = undefined;
    });
    builder.addCase(deleteMeeting.fulfilled, (state, action) => {
      const id = action.payload;
      delete state.entities[id];
      state.ids = state.ids.filter((meetingId) => meetingId !== id);
      state.total = Math.max(0, state.total - 1);
    });
    builder.addCase(deleteMeeting.rejected, (state, action) => {
      state.error = action.error.message || "Failed to delete meeting";
    });
  },
});

export const {
  resetMeetingsState,
  setFilters,
  resetFilters,
  fetchMeetingsStart,
  fetchMeetingsSuccess,
  fetchMeetingsError,
  meetingUpdated,
  meetingDeleted,
  setPage,
  setLimit,
} = meetingsSlice.actions;

export default meetingsSlice.reducer;